/**
 * {@link DataTable} for field events.
 */
import * as React from 'react';
import { Calendar } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTable } from '@/components/common/data-table';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { Button } from '@/components/forms/Button';
import { EventRow } from './EventRow';
import { EventCard } from './EventCard';
import { EventFiltersContent } from './EventFilters';
import { DetailsViewContent } from '@/components/common/details/DetailsViewContent';
import { renderEventDetails } from './renderEventDetails';
import { CancelEventModal } from './CancelEventModal';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { EventAttendanceModal } from './EventAttendanceModal';
import { useDataTable } from '@/hooks/useDataTable';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { eventsService } from '@/services/events.service';
import { cn } from '@/lib/utils';
import {
  DEFAULT_EVENT_FILTERS,
  ALL_FILTER_GROUP_IDS,
  FILTER_GROUP_LABELS,
  type EventFilters,
  type FilterGroupId,
} from './event-configs';
import {
  buildEventExportParams,
  buildEventListParams,
  getDateRangeFromFilters,
  hasEventActiveFilters,
  type EventItem,
} from './event-table-utils';
import type { EventWithParticipants } from '@/types/event.types';
import type { ViewMode } from '@/types/data-table.types';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link EventsDataTable}. */
interface EventsDataTableProps {
  showCreateButton?: boolean;
  onCreateEvent?: () => void;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditEvent?: (id: string) => void;
  enableFilters?: boolean;
  defaultFilters?: EventFilters;
  layoutWidth?: ElementWidth;
  isSection?: boolean;
  toolbarLeft?: React.ReactNode;
  /** Extra nodes rendered at the start of the section-actions bar (before filter/export/create). */
  toolbarExtra?: React.ReactNode;
  pageActions?: {
    canEditRow?: boolean;
    canCancelRow?: boolean;
    canManageAttendance?: boolean;
    canExportCsv?: boolean;
    canViewSummary?: boolean;
    canEditSummary?: boolean;
  };
}

/** {@link DataTable} for field events. */
export const EventsDataTable: React.FC<EventsDataTableProps> = ({
  showCreateButton = false,
  onCreateEvent,
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  expandedRowId: externalExpandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditEvent,
  enableFilters = true,
  defaultFilters = DEFAULT_EVENT_FILTERS,
  layoutWidth: layoutWidthProp = 'full',
  isSection = false,
  toolbarLeft,
  toolbarExtra,
  pageActions,
}) => {
  const queryClient = useQueryClient();
  const showExport = pageActions?.canExportCsv === true;

  const [cancelId, setCancelId] = React.useState<string | null>(null);
  const [attendanceEventId, setAttendanceEventId] = React.useState<string | null>(null);

  const dt = useDataTable<EventFilters, FilterGroupId>({
    tableName: 'events',
    defaultFilters,
    allFilterGroupIds: ALL_FILTER_GROUP_IDS,
    layoutWidth: layoutWidthProp,
    isSection,
    externalViewMode,
    onExternalViewModeChange: externalOnViewModeChange,
    externalExpandedRowId,
    onCloseDetails,
  });

  /** API `dateFrom`/`dateTo` from the period chip (or explicit custom dates). */
  const { dateFrom, dateTo } = React.useMemo(
    () => getDateRangeFromFilters(dt.filters),
    [dt.filters],
  );

  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['events', dt.currentPage, dt.itemsPerPage, dt.filters, dateFrom, dateTo],
    queryFn: async () => {
      return eventsService.getEvents(
        buildEventListParams(dt.filters, {
          page: dt.currentPage,
          limit: dt.itemsPerPage,
          dateFrom,
          dateTo,
        }),
      );
    },
  });

  const events: EventItem[] = (eventsData?.data as EventItem[] | undefined) ?? [];
  const totalItems = eventsData?.total || 0;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = startIndex + dt.itemsPerPage;

  /** True when any list filter differs from {@link DEFAULT_EVENT_FILTERS}. */
  const hasActiveFilters = React.useMemo(() => hasEventActiveFilters(dt.filters), [dt.filters]);

  /** Reset filters to the table defaults and jump back to page 1. */
  const clearFilters = React.useCallback(() => {
    dt.setFilters(defaultFilters);
    dt.setCurrentPage(1);
  }, [dt, defaultFilters]);

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason, detail }: { id: string; reason: string; detail?: string }) =>
      eventsService.cancelEvent(id, { reason, detail }),
    onSuccess: async () => {
      toast.success('Événement annulé avec succès');
      await queryClient.invalidateQueries({ queryKey: ['events'] });
      setCancelId(null);
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const [isExporting, setIsExporting] = React.useState(false);

  /** Download the current filter set as Excel (same query as the list, no pagination). */
  const handleExportExcel = React.useCallback(async () => {
    if (totalItems === 0) {
      toast.warning(getErrorMessage('exportEmpty'));
      return;
    }
    setIsExporting(true);
    try {
      await eventsService.exportEvents(buildEventExportParams(dt.filters, dateFrom, dateTo));
      toast.success('Export Excel réussi !');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [dt.filters, dateFrom, dateTo, totalItems]);

  /** Open the attendance modal for the given event. */
  const handleManageAttendance = React.useCallback((id: string) => {
    setAttendanceEventId(id);
  }, []);

  /** Filter panel body — changing a filter always resets pagination. */
  const customFilterRender = React.useCallback(
    () => (
      <EventFiltersContent
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        onFiltersChange={(newFilters) => {
          dt.setFilters(newFilters);
          dt.setCurrentPage(1);
        }}
      />
    ),
    [dt],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: customFilterRender,
      },
    ],
    [customFilterRender],
  );

  const {
    data: expandedEventData,
    isLoading: isLoadingExpandedEvent,
    isError: isExpandedEventError,
    error: expandedEventError,
  } = useQuery({
    queryKey: ['event', dt.expandedRowId],
    queryFn: () => eventsService.getEvent(dt.expandedRowId!),
    enabled: !!dt.expandedRowId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedEventError,
    error: expandedEventError,
    catalogKey: 'eventNotFound',
    onClose: onCloseDetails,
    entityId: dt.expandedRowId,
  });

  const expandedItem = (expandedEventData ?? null) as EventItem | null;

  return (
    <DataTable
      data={events}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="événements"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={5}
      toolbarActions={
        <>
          {enableFilters && (
            <Button
              preset="toolbar-filter"
              className={cn(dt.showFilters && 'section-action-button-active')}
              showBadge={hasActiveFilters}
              onClick={() => {
                const next = !dt.showFilters;
                dt.setShowFilters(next);
                if (!next) dt.setShowConfigure(false);
              }}
            />
          )}
          {showExport && (
            <Button
              preset="toolbar-export"
              onClick={handleExportExcel}
              loading={isExporting}
              disabled={totalItems === 0}
              tooltipText={totalItems === 0 ? getErrorMessage('exportEmpty') : 'Exporter en Excel'}
            />
          )}
          {showCreateButton && onCreateEvent && (
            <Button
              preset="toolbar-create"
              tooltipText="Créer un événement"
              onClick={() => {
                onCloseDetails?.();
                onCreateEvent();
              }}
            />
          )}
        </>
      }
      filtersPanel={
        enableFilters ? (
          <DataTableFilters
            show={dt.showFilters}
            hasActiveFilters={hasActiveFilters}
            filterGroups={filterGroups}
            onClearFilters={clearFilters}
            resultCount={totalItems}
            resultLabel="événement(s)"
            onToggleConfigure={
              dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
            }
            showConfigure={dt.showConfigure}
            configureContent={
              <div className="flex flex-wrap gap-3">
                {ALL_FILTER_GROUP_IDS.filter((id) => dt.allowedFilterIds.includes(id)).map((id) => {
                  const isOn = dt.filterVisibility[id] ?? true;
                  return (
                    <Button
                      key={id}
                      variant="custom"
                      size="sm"
                      onClick={() => dt.handleToggleFilterVisibility(id)}
                      disabled={dt.isSavingFilterVisibility}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                        dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                        isOn
                          ? 'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white'
                          : 'bg-transparent border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]',
                      )}
                    >
                      {FILTER_GROUP_LABELS[id]}
                    </Button>
                  );
                })}
              </div>
            }
          />
        ) : undefined
      }
      renderTableHead={() => (
        <tr>
          <th>Date / Horaire</th>
          <th>Établissement</th>
          <th>Activité</th>
          <th>Statut</th>
          <th className="w-28 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(event) => (
        <EventRow
          key={event.id}
          event={event}
          pageActions={pageActions}
          onView={onToggleRow}
          onEdit={onEditEvent}
          onCancel={setCancelId}
          onManageAttendance={handleManageAttendance}
        />
      )}
      expandedRowId={dt.expandedRowId}
      renderExpandedRow={(event) => (
        <DetailsViewContent>
          {renderEventDetails(event as EventWithParticipants)}
        </DetailsViewContent>
      )}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      renderCard={(event) => (
        <EventCard
          key={event.id}
          event={event}
          pageActions={pageActions}
          onView={onToggleRow}
          onEdit={onEditEvent}
          onCancel={setCancelId}
          onManageAttendance={handleManageAttendance}
        />
      )}
      emptyIcon={Calendar}
      emptyText="Aucun événement trouvé"
      title="Événements"
      sidebarMenuId="menu-events"
      isSection={isSection}
      toolbarLeft={toolbarLeft}
      toolbarExtra={toolbarExtra}
      expandedItem={expandedItem}
      isExpandedDetailLoading={isLoadingExpandedEvent}
      onCloseDetails={onCloseDetails}
      renderDetailsContent={(event) => (
        <DetailsViewContent>
          {renderEventDetails(event as EventWithParticipants)}
        </DetailsViewContent>
      )}
      modals={
        <>
          {cancelId && (
            <CancelEventModal
              eventId={cancelId}
              onClose={() => setCancelId(null)}
              onConfirm={(reason, detail) => {
                cancelMutation.mutate({ id: cancelId, reason, detail });
              }}
            />
          )}
          {attendanceEventId && (
            <BaseModal
              open={true}
              onClose={() => setAttendanceEventId(null)}
              header={{
                preset: 'validation',
                title: 'Gérer les présences',
                titleSize: 'lg',
                titleWeight: 'semibold',
              }}
              size="lg"
            >
              <EventAttendanceModal
                eventId={attendanceEventId}
                onClose={() => setAttendanceEventId(null)}
              />
            </BaseModal>
          )}
        </>
      }
    />
  );
};
